/* DOLO'S WAY: NIGHT MAZE — engine. Runners scavenge a fixed maze through one
   night; zombies stalk by scent, mutated dogs hold lanes and charge, traps and
   power items turn the dark, and the exit gate opens 30s before dawn. */
import {
  CELL, COLS, CPU_NAMES, DOG_CHARGE_SPEED, DOG_SPEED, DOWN_FRAMES, EXITS, EXIT_OPENS_AT, INVULN_FRAMES,
  MAZE, NIGHT_FRAMES, NIGHT_RUNNERS, POWER_ITEMS, RAMPAGE_AT, RAMPAGE_WINDOW, ROWS, RUNNER_COLORS, RUNNER_SPEED, SLASH_CD, SLASH_FRAMES, SLASH_REACH, skinForDay, SPAWNS, tileAt, tileCenter,
  ZOMBIE_ADD_EVERY, ZOMBIE_CAP, ZOMBIE_SPEED, ZOMBIE_START,
} from "./constants";

const dist = (ax, ay, bx, by) => Math.hypot(ax - bx, ay - by);
const tileOf = (x, y) => [Math.floor(x / CELL), Math.floor(y / CELL)];

/* BFS next-step direction on the tile grid — the maze is tiny so this is cheap */
const DIRS = [[1, 0], [-1, 0], [0, 1], [0, -1]];
function bfsNext(sx, sy, tx, ty) {
  if (sx === tx && sy === ty) return null;
  const prev = new Map();
  const q = [[sx, sy]];
  prev.set(`${sx},${sy}`, null);
  while (q.length) {
    const [cx, cy] = q.shift();
    for (const [dx, dy] of DIRS) {
      const nx = cx + dx;
      const ny = cy + dy;
      const key = `${nx},${ny}`;
      if (nx < 0 || ny < 0 || nx >= COLS || ny >= ROWS || prev.has(key)) continue;
      const t = MAZE[ny][nx];
      if (t === "#" || (t === "E" && !(nx === tx && ny === ty))) continue;
      prev.set(key, [cx, cy]);
      if (nx === tx && ny === ty) {
        let cur = [nx, ny];
        let from = prev.get(key);
        while (from && !(from[0] === sx && from[1] === sy)) {
          cur = from;
          from = prev.get(`${cur[0]},${cur[1]}`);
        }
        return [cur[0] - sx, cur[1] - sy];
      }
      q.push([nx, ny]);
    }
  }
  return null;
}

const scatter = (g, n, kind, extra = {}) => {
  let placed = 0;
  let guard = 0;
  while (placed < n && guard < 900) {
    guard += 1;
    const tx = 1 + Math.floor(g.rng() * (COLS - 2));
    const ty = 1 + Math.floor(g.rng() * (ROWS - 2));
    if (tileAt(tx, ty) !== ".") continue;
    if (g.pickups.some((p) => p.tx === tx && p.ty === ty)) continue;
    if (SPAWNS.some((s) => Math.abs(s.x - tx) + Math.abs(s.y - ty) < 3)) continue;
    const c = tileCenter(tx, ty);
    g.pickups.push({ tx, ty, x: c.x, y: c.y, kind, ...extra });
    placed += 1;
  }
};

const spawnZombie = (g) => {
  // rise from the maze edges, away from runners
  const edges = [];
  for (let x = 1; x < COLS - 1; x += 1) {
    if (tileAt(x, 1) === ".") edges.push([x, 1]);
    if (tileAt(x, ROWS - 2) === ".") edges.push([x, ROWS - 2]);
  }
  for (let y = 1; y < ROWS - 1; y += 1) {
    if (tileAt(1, y) === ".") edges.push([1, y]);
    if (tileAt(COLS - 2, y) === ".") edges.push([COLS - 2, y]);
  }
  if (!edges.length) return;
  // never rise next to a runner spawn corner or a side-door mouth — that's a cheap death
  const safe = edges.filter(
    ([x, y]) =>
      SPAWNS.every((s) => Math.abs(s.x - x) + Math.abs(s.y - y) > 4) &&
      EXITS.every((e) => Math.abs(e.x - x) + Math.abs(e.y - y) > 3),
  );
  // and never stack two of the risen on one tile
  const clear = (safe.length ? safe : edges).filter(([x, y]) => {
    const c = tileCenter(x, y);
    return g.zombies.every((z) => dist(z.x, z.y, c.x, c.y) > CELL);
  });
  const pool = clear.length ? clear : safe.length ? safe : edges;
  const pick = pool[Math.floor(g.rng() * pool.length)];
  const c = tileCenter(pick[0], pick[1]);
  g.zombies.push({ x: c.x, y: c.y, kind: Math.floor(g.rng() * 5), repath: 0, step: null, respawnIn: 0 });
};

export function freshNight(humans, total, rng = Math.random, picks = []) {
  const g = {
    frame: 0,
    rng,
    skin: skinForDay(),
    framesLeft: NIGHT_FRAMES,
    exitOpen: false,
    runners: [],
    zombies: [],
    dogs: [],
    pickups: [],
    traps: [],
    lure: null,
    banner: "NIGHT FALLS — SCAVENGE, HIDE, SURVIVE",
    bannerTimer: 150,
    over: false,
  };
  // characters first: humans get their picks, CPUs draw from the remaining roster
  const chars = [];
  for (let i = 0; i < total; i += 1) {
    const free = NIGHT_RUNNERS.map((nr) => nr.key).filter((k) => !chars.includes(k));
    let character;
    if (i < humans && picks[i] && !chars.includes(picks[i])) character = picks[i];
    else if (i < humans && !chars.includes("dolo")) character = "dolo";
    else character = free[0] || "dolo";
    chars.push(character);
  }
  // CPU callsigns never collide with a character in play tonight
  const takenNames = chars.map((k) => NIGHT_RUNNERS.find((nr) => nr.key === k)?.name).filter(Boolean);
  const cpuNamePool = CPU_NAMES.filter((n) => !takenNames.includes(n));
  const cpuNames = cpuNamePool.length ? cpuNamePool : CPU_NAMES;
  for (let i = 0; i < total; i += 1) {
    const s = SPAWNS[i];
    const c = tileCenter(s.x, s.y);
    g.runners.push({
      id: i,
      name: i < humans ? (i === 0 ? "P1" : `P${i + 1}`) : cpuNames[(i - humans) % cpuNames.length],
      human: i < humans,
      character: chars[i],
      color: RUNNER_COLORS[i],
      x: c.x, y: c.y, spawn: { ...c },
      dir: "s", moving: false,
      supplies: 0, item: null, trapCharges: 2, armor: false,
      fx: { flare: 0, adrenaline: 0, smoke: 0 },
      down: 0, invuln: 0, escaped: false,
      slashT: 0, slashCd: 0, streak: 0, streakT: 0, rampages: 0,
      repath: 0, step: null,
    });
  }
  for (let i = 0; i < ZOMBIE_START; i += 1) spawnZombie(g);
  // two mutated dogs hold the long middle lanes
  const lanes = [Math.floor(ROWS / 2), 3];
  lanes.forEach((ly, i) => {
    if (tileAt(2, ly) !== ".") return;
    const c = tileCenter(2, ly);
    g.dogs.push({ x: c.x, y: c.y, kind: i % 4, dir: 1, state: "patrol", timer: 0 });
  });
  scatter(g, 16, "can");
  scatter(g, 4, "medkit");
  scatter(g, 3, "lore");
  scatter(g, 4, "crate");
  const powers = Object.keys(POWER_ITEMS);
  powers.forEach((p) => scatter(g, 1, "power", { power: p }));
  return g;
}

const runnerHidden = (r) => {
  if (r.fx.smoke > 0) return true;
  const [tx, ty] = tileOf(r.x, r.y);
  const t = tileAt(tx, ty);
  return t === "D" || t === "V";
};

const moveEntity = (g, e, dx, dy, speed, blockSafe = false) => {
  const r = 12;
  const nx = e.x + dx * speed;
  const ny = e.y + dy * speed;
  // axis-separated tile collision with soft sliding; the horde cannot cross "S"
  const blockedAt = (x, y) => {
    const t = tileAt(tileOf(x, y)[0], tileOf(x, y)[1]);
    return t === "#" || (blockSafe && t === "S");
  };
  if (dx !== 0 && !blockedAt(nx + Math.sign(dx) * r, e.y)) e.x = nx;
  if (dy !== 0 && !blockedAt(e.x, ny + Math.sign(dy) * r)) e.y = ny;
};

/* a runner standing on the sanctuary pad is untouchable */
const runnerSafe = (r) => tileAt(tileOf(r.x, r.y)[0], tileOf(r.x, r.y)[1]) === "S";

const nearestRunner = (g, x, y, { includeHidden = false } = {}) => {
  let best = null;
  let bd = Infinity;
  for (const r of g.runners) {
    if (r.down > 0 || r.escaped) continue;
    if (runnerSafe(r)) continue; // sanctuary — the horde can't smell through the light
    if (!includeHidden && runnerHidden(r)) continue;
    const d = dist(x, y, r.x, r.y);
    if (d < bd) { bd = d; best = r; }
  }
  return best ? { runner: best, d: bd } : null;
};

export function stepNight(g, inputs, { onEvent } = {}) {
  if (g.over) return;
  g.frame += 1;
  g.framesLeft -= 1;
  if (g.bannerTimer > 0) g.bannerTimer -= 1;

  if (!g.exitOpen && g.framesLeft <= EXIT_OPENS_AT) {
    g.exitOpen = true;
    g.banner = "THE SIDE DOORS GRIND OPEN — LEFT OR RIGHT, GET OUT BEFORE DAWN";
    g.bannerTimer = 180;
    onEvent?.("gate");
  }
  if (g.framesLeft % ZOMBIE_ADD_EVERY === 0 && g.zombies.length < ZOMBIE_CAP) {
    spawnZombie(g);
    g.banner = "THE HORDE THICKENS — ANOTHER SCAVENGER RISES";
    g.bannerTimer = 90;
    onEvent?.("rise");
  }
  // supply trickle so the board never starves
  if (g.frame % (8 * 60) === 0 && g.pickups.filter((p) => p.kind === "can").length < 10) scatter(g, 3, "can");

  const flareSources = g.runners.filter((r) => r.fx.flare > 0 && r.down <= 0 && !r.escaped);

  // ---- runners ----
  for (const r of g.runners) {
    if (r.escaped) continue;
    if (r.down > 0) {
      r.down -= 1;
      if (r.down === 0) {
        r.x = r.spawn.x; r.y = r.spawn.y;
        r.invuln = INVULN_FRAMES;
      }
      continue;
    }
    if (r.invuln > 0) r.invuln -= 1;
    Object.keys(r.fx).forEach((k) => { if (r.fx[k] > 0) r.fx[k] -= 1; });

    const speed = RUNNER_SPEED * (r.fx.adrenaline > 0 ? 1.6 : 1);
    let dx = 0; let dy = 0;
    let useKey = false; let trapKey = false; let slashKey = false;

    if (r.human) {
      const keys = inputs[r.id] || {};
      dx = (keys.right ? 1 : 0) - (keys.left ? 1 : 0);
      dy = (keys.down ? 1 : 0) - (keys.up ? 1 : 0);
      useKey = keys.use;
      trapKey = keys.trap;
      slashKey = keys.slash;
    } else {
      // CPU rival — greed with a survival instinct
      r.repath -= 1;
      let target = null;
      const zNear = g.zombies.filter((z) => z.respawnIn <= 0 && dist(z.x, z.y, r.x, r.y) < 4 * CELL);
      const dNear = g.dogs.filter((d) => d.state === "charge" && dist(d.x, d.y, r.x, r.y) < 4 * CELL);
      if ((zNear.length || dNear.length) && r.item === "flare" && dist(zNear[0]?.x ?? 1e9, zNear[0]?.y ?? 1e9, r.x, r.y) < 2.5 * CELL) {
        useKey = true;
      }
      if (g.exitOpen) {
        // nearest of the two side doors
        const [cx, cy] = tileOf(r.x, r.y);
        target = EXITS.reduce((best, e) => (Math.abs(e.x - cx) + Math.abs(e.y - cy) < Math.abs(best.x - cx) + Math.abs(best.y - cy) ? e : best));
      } else if (zNear.length || dNear.length) {
        const z = zNear[0] || dNear[0];
        // run to the opposite direction / nearest hide
        let hide = null; let hd = Infinity;
        for (let y = 0; y < ROWS; y += 1) for (let x = 0; x < COLS; x += 1) {
          const t = tileAt(x, y);
          if (t === "D" || t === "V") {
            const dd = Math.abs(x * CELL - r.x) + Math.abs(y * CELL - r.y);
            if (dd < hd) { hd = dd; hide = { x, y }; }
          }
        }
        target = hide || { x: Math.max(1, Math.min(COLS - 2, Math.floor((r.x + (r.x - z.x)) / CELL))), y: Math.max(1, Math.min(ROWS - 2, Math.floor((r.y + (r.y - z.y)) / CELL))) };
      } else {
        let bestP = null; let bd = Infinity;
        for (const p of g.pickups) {
          const dd = dist(p.x, p.y, r.x, r.y);
          if (dd < bd) { bd = dd; bestP = p; }
        }
        if (bestP) target = { x: bestP.tx, y: bestP.ty };
      }
      if (target) {
        const [rtx, rty] = tileOf(r.x, r.y);
        if (r.repath <= 0 || !r.step) {
          r.step = bfsNext(rtx, rty, target.x, target.y);
          r.repath = 28;
        }
        if (r.step) { dx = r.step[0]; dy = r.step[1]; }
        if (dx === 0 && dy === 0) { r.step = null; r.repath = 0; }
      }
      // CPU rivals drop traps when hounded
      if (zNear.length && r.trapCharges > 0 && g.rng() < 0.006) trapKey = true;
      // and cut their way out when the horde gets within blade range
      if (r.slashCd <= 0 && g.rng() < 0.6) {
        const zClose = g.zombies.some((z) => z.respawnIn <= 0 && dist(z.x, z.y, r.x, r.y) < SLASH_REACH * 0.9);
        const dClose = g.dogs.some((dg) => dist(dg.x, dg.y, r.x, r.y) < SLASH_REACH * 0.9);
        if (zClose || dClose) slashKey = true;
      }
    }

    if (dx !== 0 && dy !== 0) { dx *= 0.7071; dy *= 0.7071; }
    moveEntity(g, r, dx, dy, speed);
    // sanctuary crossing — the lit pad at the maze heart is untouchable ground
    const onPad = runnerSafe(r);
    if (onPad && !r.wasSafe && r.human) {
      g.banner = "SANCTUARY — THE HORDE CAN'T CROSS";
      g.bannerTimer = 70;
    }
    r.wasSafe = onPad;
    r.moving = dx !== 0 || dy !== 0;
    if (dx > 0.1) r.dir = "e";
    else if (dx < -0.1) r.dir = "w";
    else if (dy > 0.1) r.dir = "s";
    else if (dy < -0.1) r.dir = "n";

    // sword slash — a short arc in the facing direction
    if (r.slashCd > 0) r.slashCd -= 1;
    if (r.slashT > 0) r.slashT -= 1;
    if (r.streakT > 0) {
      r.streakT -= 1;
      if (r.streakT === 0) r.streak = 0; // the chain went cold
    }
    if (slashKey && r.slashCd <= 0) {
      r.slashT = SLASH_FRAMES;
      r.slashCd = SLASH_CD;
      // AUTO-AIM — the blade snaps to the nearest threat in reach; facing only
      // matters when nothing is close, so a swing never whiffs behind a runner
      let aim = null;
      let bestD = Infinity;
      for (const z of g.zombies) {
        if (z.respawnIn > 0) continue;
        const d = dist(z.x, z.y, r.x, r.y);
        if (d < SLASH_REACH && d < bestD) { bestD = d; aim = { x: z.x - r.x, y: z.y - r.y }; }
      }
      for (const dg of g.dogs) {
        const d = dist(dg.x, dg.y, r.x, r.y);
        if (d < SLASH_REACH && d < bestD) { bestD = d; aim = { x: dg.x - r.x, y: dg.y - r.y }; }
      }
      let fx = r.dir === "e" ? 1 : r.dir === "w" ? -1 : 0;
      let fy = r.dir === "s" ? 1 : r.dir === "n" ? -1 : 0;
      if (aim) {
        const mag = Math.hypot(aim.x, aim.y) || 1;
        fx = aim.x / mag;
        fy = aim.y / mag;
        // face the target so the rendered crescent follows the blade
        r.dir = Math.abs(fx) > Math.abs(fy) ? (fx > 0 ? "e" : "w") : (fy > 0 ? "s" : "n");
      }
      let hit = false;
      // the red blade ELIMINATES — slashed zombies are destroyed for good, no rise-back
      const slain = [];
      for (const z of g.zombies) {
        if (z.respawnIn > 0) continue;
        const ddx = z.x - r.x;
        const ddy = z.y - r.y;
        const d = Math.hypot(ddx, ddy);
        if (d < SLASH_REACH && d > 0 && (ddx * fx + ddy * fy) / d > 0.1) slain.push(z);
      }
      for (const z of slain) {
        const i = g.zombies.indexOf(z);
        if (i >= 0) g.zombies.splice(i, 1);
        hit = true;
      }
      if (slain.length) {
        for (let i = 0; i < slain.length; i += 1) {
          r.streak += 1;
          if (r.streak >= RAMPAGE_AT) r.rampages += 1; // +1 PT bounty per rampage kill
        }
        r.streakT = RAMPAGE_WINDOW;
      }
      for (const dg of g.dogs) {
        const ddx = dg.x - r.x;
        const ddy = dg.y - r.y;
        const d = Math.hypot(ddx, ddy);
        if (d < SLASH_REACH && d > 0 && (ddx * fx + ddy * fy) / d > 0.1) {
          dg.stun = 240;
          dg.state = "patrol";
          hit = true;
        }
      }
      const rampage = slain.length > 0 && r.streak >= RAMPAGE_AT;
      g.banner = hit
        ? rampage
          ? `RAMPAGE ×${r.streak} — ${r.name} IS UNSTOPPABLE`
          : slain.length
            ? `${r.name}'S RED BLADE ELIMINATES — NO RISE BACK`
            : `${r.name} CUTS THE DARK DOWN`
        : `${r.name} SWINGS AT SHADOWS`;
      g.bannerTimer = rampage ? 110 : hit ? 70 : 40;
      onEvent?.(rampage ? "rampage" : hit ? "slash-hit" : "slash");
    }

    // use power item
    if (useKey && r.item) {
      const power = r.item;
      r.item = null;
      if (power === "armor") {
        r.armor = true;
        g.banner = `${r.name} PLATES UP — SCRAP ARMOR ARMED`;
      } else if (power === "lure") {
        const [tx, ty] = tileOf(r.x, r.y);
        const ahead = tileOf(r.x + (r.dir === "e" ? CELL * 2 : r.dir === "w" ? -CELL * 2 : 0), r.y + (r.dir === "s" ? CELL * 2 : r.dir === "n" ? -CELL * 2 : 0));
        const spot = tileAt(ahead[0], ahead[1]) === "." ? ahead : [tx, ty];
        const c = tileCenter(spot[0], spot[1]);
        g.lure = { x: c.x, y: c.y, timer: POWER_ITEMS.lure.duration };
        g.banner = `${r.name} CRANKS A BOOMBOX LURE — THE HORDE TURNS`;
      } else {
        r.fx[power] = POWER_ITEMS[power].duration;
        g.banner = `${r.name} FIRES ${POWER_ITEMS[power].name}`;
      }
      g.bannerTimer = 100;
      onEvent?.("power");
    }
    // set trap
    if (trapKey && r.trapCharges > 0) {
      const [tx, ty] = tileOf(r.x, r.y);
      if (tileAt(tx, ty) === "." && !g.traps.some((t) => t.tx === tx && t.ty === ty)) {
        r.trapCharges -= 1;
        g.traps.push({ tx, ty, ...tileCenter(tx, ty), armed: 20 });
        onEvent?.("trap");
      }
    }
    // pickups
    for (let i = g.pickups.length - 1; i >= 0; i -= 1) {
      const p = g.pickups[i];
      if (dist(p.x, p.y, r.x, r.y) < 22) {
        g.pickups.splice(i, 1);
        if (p.kind === "can") r.supplies += 1;
        else if (p.kind === "medkit") r.supplies += 2;
        else if (p.kind === "lore") r.supplies += 3;
        else if (p.kind === "crate") r.trapCharges += 2;
        else if (p.kind === "power" && !r.item) r.item = p.power;
        else continue;
        onEvent?.("pickup");
      }
    }
    // exit — either side door, left or right
    if (g.exitOpen) {
      const [tx, ty] = tileOf(r.x, r.y);
      if (tileAt(tx, ty) === "E") {
        const door = EXITS.find((e) => e.x === tx && e.y === ty);
        r.escaped = true;
        g.banner = `${r.name} ESCAPES THROUGH THE ${door ? door.name : "SIDE DOOR"}`;
        g.bannerTimer = 110;
        onEvent?.("escape");
      }
    }
  }

  // ---- lure ----
  if (g.lure) {
    g.lure.timer -= 1;
    if (g.lure.timer <= 0) {
      // the boombox blows — the blast drops nearby zombies
      g.zombies = g.zombies.filter((z) => {
        if (dist(z.x, z.y, g.lure.x, g.lure.y) < 2 * CELL) {
          z.respawnIn = 480;
          z.x = -999;
          return true; // stays in list, off-board while respawning
        }
        return true;
      });
      g.banner = "THE LURE BLOWS — THE HORDE SCATTERS";
      g.bannerTimer = 80;
      onEvent?.("boom");
      g.lure = null;
    }
  }

  // ---- zombies ----
  for (const z of g.zombies) {
    if (z.respawnIn > 0) {
      z.respawnIn -= 1;
      if (z.respawnIn === 0) {
        z.x = -1; z.y = -1;
        spawnZombie(g);
        g.zombies.splice(g.zombies.indexOf(z), 1);
      }
      continue;
    }
    let tx = null; let ty = null;
    if (g.lure) {
      [tx, ty] = tileOf(g.lure.x, g.lure.y);
    } else {
      const hit = nearestRunner(g, z.x, z.y);
      if (hit) {
        // flare repels
        if (flareSources.includes(hit.runner)) {
          const away = Math.sign(z.x - hit.runner.x) || 1;
          moveEntity(g, z, away, Math.sign(z.y - hit.runner.y) * 0.4, ZOMBIE_SPEED, true);
          continue;
        }
        [tx, ty] = tileOf(hit.runner.x, hit.runner.y);
      }
    }
    if (tx !== null) {
      z.repath -= 1;
      const [zx, zy] = tileOf(z.x, z.y);
      if (z.repath <= 0 || !z.step) {
        z.step = bfsNext(zx, zy, tx, ty);
        z.repath = 24;
      }
      if (z.step) moveEntity(g, z, z.step[0], z.step[1], ZOMBIE_SPEED, true);
    } else {
      // nobody to smell — drift
      moveEntity(g, z, Math.sin((g.frame + z.x) * 0.01), Math.cos((g.frame + z.y) * 0.01), ZOMBIE_SPEED * 0.4, true);
    }
    // traps
    const [zx, zy] = tileOf(z.x, z.y);
    const trap = g.traps.find((t) => t.tx === zx && t.ty === zy && t.armed <= 0);
    if (trap) {
      g.traps.splice(g.traps.indexOf(trap), 1);
      z.respawnIn = 480;
      z.x = -999;
      g.banner = "A SCAVENGER HITS A TRAP — DOWN";
      g.bannerTimer = 70;
      onEvent?.("trapped");
      continue;
    }
    // takedowns — never on the sanctuary pad
    for (const r of g.runners) {
      if (r.down > 0 || r.escaped || r.invuln > 0 || runnerSafe(r)) continue;
      if (dist(z.x, z.y, r.x, r.y) < 18) downRunner(g, r, onEvent);
    }
  }

  // ---- dogs ----
  for (const d of g.dogs) {
    if (d.stun > 0) { d.stun -= 1; continue; }
    const [dtx, dty] = tileOf(d.x, d.y);
    const trap = g.traps.find((t) => t.tx === dtx && t.ty === dty && t.armed <= 0);
    if (trap) {
      g.traps.splice(g.traps.indexOf(trap), 1);
      d.stun = 240;
      d.state = "patrol";
      g.banner = "A MUTATED DOG SNARLS INTO A TRAP";
      g.bannerTimer = 70;
      onEvent?.("trapped");
      continue;
    }
    if (d.state === "patrol") {
      d.x += d.dir * DOG_SPEED;
      const [nx] = tileOf(d.x + d.dir * 16, d.y);
      if (tileAt(nx, dty) === "#" || tileAt(nx, dty) === "S") d.dir *= -1;
      // charge when a visible runner shares the lane
      const hit = nearestRunner(g, d.x, d.y);
      if (hit && Math.abs(hit.runner.y - d.y) < CELL * 0.7 && hit.d < 6 * CELL) {
        d.state = "charge";
        d.timer = 110;
        d.chargeDir = Math.sign(hit.runner.x - d.x) || 1;
        onEvent?.("charge");
      }
    } else if (d.state === "charge") {
      d.timer -= 1;
      d.x += d.chargeDir * DOG_CHARGE_SPEED;
      const [nx] = tileOf(d.x + d.chargeDir * 16, d.y);
      if (tileAt(nx, dty) === "#" || tileAt(nx, dty) === "S" || d.timer <= 0) {
        d.state = "cool";
        d.timer = 170;
      }
      for (const r of g.runners) {
        if (r.down > 0 || r.escaped || r.invuln > 0 || runnerSafe(r)) continue;
        if (dist(d.x, d.y, r.x, r.y) < 22) downRunner(g, r, onEvent);
      }
    } else {
      d.timer -= 1;
      if (d.timer <= 0) d.state = "patrol";
    }
  }

  // traps arm
  for (const t of g.traps) if (t.armed > 0) t.armed -= 1;

  // ---- dawn ----
  if (g.framesLeft <= 0) {
    g.over = true;
    const escaped = g.runners.filter((r) => r.escaped);
    g.banner = escaped.length ? "DAWN BREAKS — THE LIVING SEE THE SUN" : "DAWN — NOBODY LEFT THE MAZE";
    g.bannerTimer = 300;
    onEvent?.("dawn");
  }
}

function downRunner(g, r, onEvent) {
  if (r.armor) {
    r.armor = false;
    r.invuln = 90;
    g.banner = `${r.name}'S SCRAP ARMOR TAKES THE HIT`;
    g.bannerTimer = 80;
    onEvent?.("armor");
    return;
  }
  r.down = DOWN_FRAMES;
  r.streak = 0; // going down snaps the chain
  r.streakT = 0;
  const drop = Math.floor(r.supplies * 0.4);
  r.supplies -= drop;
  // dropped supplies scatter around the body — anyone can grab them
  for (let i = 0; i < drop; i += 1) {
    const [tx0, ty0] = tileOf(r.x, r.y);
    const tx = Math.max(1, Math.min(COLS - 2, tx0 + Math.floor(g.rng() * 3) - 1));
    const ty = Math.max(1, Math.min(ROWS - 2, ty0 + Math.floor(g.rng() * 3) - 1));
    if (tileAt(tx, ty) === ".") g.pickups.push({ tx, ty, ...tileCenter(tx, ty), kind: "can" });
  }
  r.item = r.item && g.rng() < 0.5 ? null : r.item;
  g.banner = `${r.name} IS DRAGGED DOWN — SUPPLIES SPILLED`;
  g.bannerTimer = 90;
  onEvent?.("down");
}

export const rankings = (g) =>
  [...g.runners].sort((a, b) => (b.escaped - a.escaped) || (b.supplies - a.supplies));

/* pts payout for the human runner(s): 1 PT per 3 supplies, +5 for escaping */
export const payoutFor = (r) => Math.min(50, Math.floor(r.supplies / 3) + (r.escaped ? 5 : 0) + (r.rampages || 0));
